import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { LanguageProvider } from '@/hooks/useLanguage';
import { AccessibilityProvider } from '@/hooks/useAccessibility';
import { queryClient } from './lib/queryClient';
import App from './App';
import './index.css';
import React from 'react';
import ReactDOM from 'react-dom';

// Initialize Sentry FIRST
import { initSentry } from './lib/sentry';
import ErrorBoundary from './components/ErrorBoundary';
initSentry();

// PWA Service Worker Registration
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', async () => {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
      });

      // Handle service worker updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New content is available, notify user
              console.warn('New content available. Please refresh.');
              // You can show a toast notification here
            }
          });
        }
      });

      console.warn('Service Worker registered successfully');
    } catch (error) {
      console.error('Service Worker registration failed:', error);
    }
  });
}

// Axe-core runtime accessibility audit (development only).
// Logs WCAG violations to the browser console so issues are caught while
// iterating on UI changes. Does not run in production builds.
if (import.meta.env.DEV) {
  (async () => {
    try {
      const { default: axe } = await import('@axe-core/react');
      // 1000ms debounce to avoid spamming during HMR-triggered re-renders
      axe(React, ReactDOM, 1000, {
        rules: [
          // Allow color-contrast audit (the high-contrast mode is opt-in)
          { id: 'color-contrast', enabled: true },
        ],
      });
    } catch (err) {
      console.warn('axe-core dev audit not available:', err);
    }
  })();
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <LanguageProvider>
          <AccessibilityProvider>
            <TooltipProvider>
              <App />
              <Toaster />
            </TooltipProvider>
          </AccessibilityProvider>
        </LanguageProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>
);
